import {AllHealthScore} from '../../components/slides/AllHealthScore';
import {CarouselPage} from '../../components/slides/CarouselPage';
import ShowAllProductsPage from '../../components/slides/ShowAllProductsPage';
import {SLIDE_KEYS, HEALTH_CATEGORIES} from '../../utils/constants';

// Add this function to generate categories
function generateCategories(resultData) {
  if (!resultData) return [];

  const {symptoms, healthMetrics, features} = resultData;
  const categories = [];

  // Only add Health Score if there are symptoms
  if (symptoms && symptoms.length > 0) {
    categories.push({
      id: 'overall-health',
      name: 'Health Score',
    });
  }

  // Add health metric categories
  if (healthMetrics?.heartHealth) {
    categories.push({id: 'heart-health', name: 'Heart Health'});
  }

  if (healthMetrics?.bodyFunction) {
    categories.push({id: 'body-function', name: 'Body Function'});
  }

  if (healthMetrics?.physicalHealth) {
    categories.push({id: 'physical-health', name: 'Physical Health'});
  }

  if (healthMetrics?.diseaseRisk) {
    categories.push({id: 'disease-risk', name: 'Disease Risk'});
  }

  if (healthMetrics?.metabolicHealth) {
    categories.push({id: 'metabolic-health', name: 'Metabolic Health'});
  }

  // Add products if enabled
  if (features?.showProducts) {
    categories.push({id: 'products', name: 'Products'});
  }

  return categories;
}

// Helper function to find the weakest health metric
function getWeakestHealthMetric(healthMetrics) {
  if (!healthMetrics) return null;

  const sortedMetrics = Object.entries(healthMetrics)
    .filter(([key, metric]) => metric && typeof metric.value === 'number')
    .sort((a, b) => a[1].value - b[1].value);

  return sortedMetrics.length > 0 ? sortedMetrics[0][1] : null;
}

// Helper function to create a products item with meaningful data
function createProductsItem(healthMetrics, results) {
  const weakestMetric = getWeakestHealthMetric(healthMetrics);

  return {
    name: 'Top Products for You',
    value: weakestMetric?.value || 0,
    rating: weakestMetric?.rating || 'Fair',
    resultHeading: 'Recommended Products',
    description:
      'Our recommendations to elevate your health in your weakest health categories based on your assessment results. These products are specifically chosen to help improve your overall wellness.',
    results: [
      {
        name: 'Product Recommendations',
        description: `Based on your health assessment, we've identified products that can help improve your ${weakestMetric?.name || 'overall health'}. These recommendations are tailored to address your specific health needs.`,
        value: `${weakestMetric?.value || 0}`,
        symbol: '/others/shopping-cart.svg',
        label: 'score',
      },
    ],
    category: 'Products',
    isGameCompleted: true,
    userAssessmentID: results?.assessmentId || results?.sessionId,
  };
}

export const createSlides = (resultData, navigationHandlers = {}) => {
  const {healthMetrics, symptoms, features, isGameCompleted, results, organization} = resultData;
  const slides = [];

  // Generate categories for this result data
  const categories = generateCategories(resultData);
  let currentSlideIndex = 0;

  // Calculate the index of the products slide
  const productsSlideIndex = categories.findIndex(cat => cat.id === 'products');

  // Only add overall health score if there are symptoms
  if (symptoms && symptoms.length > 0) {
    slides.push({
      key: SLIDE_KEYS.ALL_HEALTH,
      component: AllHealthScore,
      props: {
        value: results.overallHealth.result,
        title: results.overallHealth.title,
        symptoms: symptoms || [],
        isShowAllResultsCard: true,
        findProductBtnHandlerForSymptomPage: () => {
          // Navigate to products slide if it exists
          if (navigationHandlers.navigateToSlide && productsSlideIndex !== -1) {
            navigationHandlers.navigateToSlide(productsSlideIndex);
          }
        },
        config: organization.config,
        organization,
        onBackBtnClick: () => {},
        getCategories: () => categories,
        activeCategoryIndex: currentSlideIndex,
        handleCategorySelect: null, // This will be set by the parent component
        // Add the navigation handler prop
        onNavigateToProducts: () => {
          if (navigationHandlers.navigateToSlide && productsSlideIndex !== -1) {
            navigationHandlers.navigateToSlide(productsSlideIndex);
          }
        },
      },
    });
    currentSlideIndex++;
  }

  // Add health category slides
  const healthCategories = [
    {key: SLIDE_KEYS.HEART_HEALTH, metric: healthMetrics.heartHealth},
    {key: SLIDE_KEYS.BODY_FUNCTION, metric: healthMetrics.bodyFunction},
    {key: SLIDE_KEYS.PHYSICAL_HEALTH, metric: healthMetrics.physicalHealth},
    {key: SLIDE_KEYS.DISEASE_RISK, metric: healthMetrics.diseaseRisk},
    {key: SLIDE_KEYS.DIABETES_RISK, metric: healthMetrics.metabolicHealth},
  ];

  healthCategories.forEach(({key, metric}) => {
    if (metric) {
      slides.push({
        key,
        component: CarouselPage,
        props: {
          item: {
            ...metric,
            isGameCompleted,
            userAssessmentID: results.assessmentId || results.sessionId,
            resultHeading: `${metric.name}`,
          },
          isShowAllResultsCard: true,
          handleUpgradeToSeeResults: () => {},
          onBackBtnClick: () => {},
          isHandleOverlayVisibilty: () => {},
          getCategories: () => categories,
          activeCategoryIndex: currentSlideIndex, // Use the correct index
          handleCategorySelect: null, // This will be set by the parent component
          config: organization.config,
          organization,
          features,
        },
      });
      currentSlideIndex++;
    }
  });

  // Add products slide with proper item prop
  if (features.showProducts) {
    const productsItem = createProductsItem(healthMetrics, results);

    slides.push({
      key: SLIDE_KEYS.PRODUCTS,
      component: ShowAllProductsPage,
      props: {
        // Add the item prop for the footer
        item: productsItem,
        // Existing props
        sortedMetricsByHealthOrder: calculateHealthOrder(healthMetrics),
        blurAndHide: false,
        handleNavigation: () => {},
        onBackBtnClick: () => {},
        getCategories: () => categories,
        handleCategorySelect: null,
        activeCategoryIndex: currentSlideIndex,
        config: organization.config,
        organization,
        // Additional props that might be useful
        isShowAllResultsCard: true,
        handleUpgradeToSeeResults: () => {},
        isHandleOverlayVisibilty: () => {},
        features,
      },
    });
  }
  return slides;
};

export function getCategories(resultData) {
  // Simply call the generateCategories function
  return generateCategories(resultData);
}

function calculateHealthOrder(healthMetrics) {
  return Object.entries(healthMetrics)
    .filter(([key, metric]) => metric && typeof metric.value === 'number')
    .map(([key, metric]) => ({
      cardKey: key,
      healthName: metric.name,
      fieldValue: metric.value,
      category: metric.name,
      healthOrderNo: getHealthOrderNumber(metric.name, metric.value),
    }))
    .sort((a, b) => a.healthOrderNo - b.healthOrderNo);
}

function getHealthOrderNumber(healthName, value) {
  // For disease risk, lower values are better, so invert
  if (healthName === 'Overall Disease Risk') {
    return 100 - value;
  }
  return value;
}
